import clsx from "clsx";
import { LoaderCircle } from "lucide-react";
import {
  createContext,
  useContext,
  type ComponentPropsWithRef,
  type ReactNode,
} from "react";

/**
 * Buttons and the control-size context shared by every interactive primitive.
 *
 * Two sizes exist because two devices exist: `desktop` (Parts, keyboard and
 * mouse, at least 28×28 CSS px) and `touch` (Surface Pro, at least 44×44 CSS
 * px). A workspace sets its size once with {@link ControlSizeProvider}; every
 * control below reads it, and any control can still override it locally.
 */

export type ControlSize = "desktop" | "touch";
export type ButtonVariant = "primary" | "secondary" | "destructive" | "quiet";

const ControlSizeContext = createContext<ControlSize>("desktop");

export function ControlSizeProvider({
  size,
  children,
}: {
  size: ControlSize;
  children: ReactNode;
}) {
  return (
    <ControlSizeContext.Provider value={size}>
      {children}
    </ControlSizeContext.Provider>
  );
}

/** The control size in effect: an explicit prop wins over the nearest provider. */
export function useControlSize(override?: ControlSize): ControlSize {
  const inherited = useContext(ControlSizeContext);
  return override ?? inherited;
}

export interface ButtonProps extends Omit<
  ComponentPropsWithRef<"button">,
  "children"
> {
  variant?: ButtonVariant;
  size?: ControlSize;
  /**
   * A mutation is in flight. The button is disabled so it cannot fire twice,
   * announces `aria-busy`, shows a spinner and keeps its label so the layout
   * does not jump and screen readers still know what is running.
   */
  busy?: boolean;
  /** Leading icon; hidden from the accessibility tree because the label carries meaning. */
  icon?: ReactNode;
  /** Icon-only buttons render the label visually hidden; the label still names the control. */
  iconOnly?: boolean;
  children: ReactNode;
}

export function Button({
  variant = "secondary",
  size,
  busy = false,
  icon,
  iconOnly = false,
  type = "button",
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) {
  const resolvedSize = useControlSize(size);
  const isDisabled = Boolean(disabled) || busy;

  return (
    <button
      {...rest}
      type={type}
      className={clsx("button", className)}
      data-variant={variant}
      data-size={resolvedSize}
      data-busy={busy ? "true" : undefined}
      data-icon-only={iconOnly ? "true" : undefined}
      disabled={isDisabled}
      aria-busy={busy ? "true" : undefined}
    >
      {busy ? (
        <LoaderCircle
          className="button__spinner"
          aria-hidden="true"
          focusable="false"
        />
      ) : icon ? (
        <span className="button__icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className={iconOnly ? "visually-hidden" : "button__label"}>
        {children}
      </span>
    </button>
  );
}

/**
 * Attributes for an element that must look like a button but is not one — a
 * react-router `<Link>` presented as an action. Spreading these keeps the
 * visual language in one place without giving a link the button role.
 */
export function buttonAttributes(
  options: {
    variant?: ButtonVariant;
    size?: ControlSize;
    className?: string;
  } = {},
): {
  className: string;
  "data-variant": ButtonVariant;
  "data-size": ControlSize;
} {
  return {
    className: clsx("button", options.className),
    "data-variant": options.variant ?? "secondary",
    "data-size": options.size ?? "desktop",
  };
}
